import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  FileArchive, Building2, FileText, ShieldAlert, Check, Download,
  MessageSquareWarning, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

/**
 * NBPortfolio — audit-side view of every change-control that has been
 * pushed to the Notified-Body portfolio. QA can update review status and
 * download a one-file PDF per delta. Full CRUD lives at:
 *   POST   /api/changes/:ch_id/nb-portfolio/push   (from ChangeControl)
 *   GET    /api/nb-portfolio/deltas                (this page)
 *   GET    /api/nb-portfolio/deltas/:id            (drill-in)
 *   PATCH  /api/nb-portfolio/deltas/:id            (status + notes)
 *   GET    /api/nb-portfolio/deltas/:id/pdf        (audit PDF)
 */
const STATUS_META = {
  pending_review:   { label: "Pending NB review",  cls: "bg-amber-50 border-amber-200 text-amber-800" },
  acknowledged:     { label: "Acknowledged",       cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  requires_change:  { label: "Requires change",    cls: "bg-red-50 border-red-200 text-red-800" },
  closed:           { label: "Closed",             cls: "bg-slate-100 border-slate-300 text-slate-700" },
};

export default function NBPortfolio() {
  const [deltas, setDeltas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [statusEdit, setStatusEdit] = useState(null); // { delta, status, notes }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nb-portfolio/deltas");
      setDeltas(data || []);
    } catch (e) {
      logger.warn("nb-deltas load:", e?.response?.status);
      toast.error("Failed to load NB deltas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (delta) => {
    try {
      const resp = await api.get(`/nb-portfolio/deltas/${delta.delta_id}/pdf`,
        { responseType: "blob", timeout: 30000 });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `nb_portfolio_${delta.delta_id}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Delta PDF exported · ${resp.headers["x-audit-event-id"] || "logged"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const saveStatus = async () => {
    if (!statusEdit) return;
    setSaving(true);
    try {
      await api.patch(`/nb-portfolio/deltas/${statusEdit.delta.delta_id}`, {
        status: statusEdit.status,
        notes: statusEdit.notes || null,
      });
      toast.success("NB delta status updated");
      setStatusEdit(null);
      load();
      if (active?.delta_id === statusEdit.delta.delta_id) {
        // Refresh drawer if open
        const { data } = await api.get(`/nb-portfolio/deltas/${statusEdit.delta.delta_id}`);
        setActive(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="nb-portfolio-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="vq-overline">Audit</div>
            <h1 className="font-display font-bold text-3xl text-slate-900 mt-1">
              <Building2 className="inline h-7 w-7 mr-2 text-vq-blue" />
              Notified-Body portfolio
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every approved change control pushed here becomes an immutable
              audit-ready delta. NB reviewers see the full change + linked
              risks + linked SOP versions in one PDF.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="nb-portfolio-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="vq-card p-10 text-center text-slate-500" data-testid="nb-portfolio-loading">
            Loading NB portfolio…
          </div>
        ) : deltas.length === 0 ? (
          <div className="vq-card p-10 text-center" data-testid="nb-portfolio-empty">
            <FileArchive className="mx-auto h-10 w-10 text-slate-300 mb-3" />
            <div className="font-display font-bold text-slate-900">No deltas pushed yet</div>
            <div className="text-sm text-slate-500 mt-1">
              Approved a change control? Push it from <Link className="text-vq-blue underline" to="/changes">Change Control</Link>
              &nbsp;to publish an NB-ready delta here.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {deltas.map((d) => (
              <NBDeltaRow
                key={d.delta_id}
                delta={d}
                onOpen={() => setActive(d)}
                onEditStatus={() => setStatusEdit({ delta: d, status: d.status, notes: d.notes || "" })}
                onDownload={() => downloadPdf(d)}
              />
            ))}
          </div>
        )}

        {/* Drill-in drawer */}
        <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
          <DialogContent className="max-w-3xl" data-testid="nb-delta-detail">
            {active && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display">{active.change_title || active.change_id}</DialogTitle>
                  <DialogDescription className="text-xs font-mono">
                    {active.delta_id} · Pushed by {active.pushed_by_email || "—"} · {new Date(active.pushed_at).toLocaleString()}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2 text-sm">
                  <NBDetailSection title={`Linked risks (${(active.risks || []).length})`} icon={ShieldAlert}>
                    {(active.risks || []).length === 0 ? (
                      <div className="text-xs text-slate-400">No risks captured.</div>
                    ) : (
                      <ul className="text-xs space-y-1">
                        {active.risks.map((r) => (
                          <li key={r.risk_id} className="font-mono text-slate-700">
                            <span className="text-slate-400">{r.risk_id}</span> — {r.title}
                            {" "}
                            <Badge className="ml-1 text-[9px]" variant="outline">
                              {r.residual_risk_level || r.initial_risk_level || "—"}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </NBDetailSection>
                  <NBDetailSection title={`Linked documents (${(active.documents || []).length})`} icon={FileText}>
                    {(active.documents || []).length === 0 ? (
                      <div className="text-xs text-slate-400">No documents captured.</div>
                    ) : (
                      <ul className="text-xs space-y-1">
                        {active.documents.map((d) => (
                          <li key={d.document_id} className="font-mono text-slate-700">
                            <span className="text-slate-400">{d.document_id}</span> — {d.title} · v{d.version || "—"} · {d.state}
                          </li>
                        ))}
                      </ul>
                    )}
                  </NBDetailSection>
                  <NBDetailSection title="NB review notes" icon={MessageSquareWarning}>
                    <div className="text-xs whitespace-pre-wrap text-slate-700">
                      {active.notes || <span className="text-slate-400">No notes yet.</span>}
                    </div>
                  </NBDetailSection>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Status edit modal */}
        <Dialog open={!!statusEdit} onOpenChange={(o) => { if (!o) setStatusEdit(null); }}>
          <DialogContent className="max-w-md" data-testid="nb-status-dialog">
            <DialogHeader>
              <DialogTitle>Update NB review status</DialogTitle>
              <DialogDescription className="text-xs">
                Mirror what the Notified-Body reviewer told you. Audit-logged.
              </DialogDescription>
            </DialogHeader>
            {statusEdit && (
              <div className="space-y-3 mt-2">
                <select
                  value={statusEdit.status}
                  onChange={(e) => setStatusEdit({ ...statusEdit, status: e.target.value })}
                  className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                  data-testid="nb-status-select"
                >
                  {Object.entries(STATUS_META).map(([k, m]) => (
                    <option key={k} value={k}>{m.label}</option>
                  ))}
                </select>
                <Textarea
                  rows={4}
                  placeholder="NB feedback / next steps…"
                  value={statusEdit.notes}
                  onChange={(e) => setStatusEdit({ ...statusEdit, notes: e.target.value })}
                  data-testid="nb-status-notes"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStatusEdit(null)}>Cancel</Button>
                  <Button size="sm" onClick={saveStatus} disabled={saving}
                          className="bg-vq-blue text-white hover:bg-vq-blue/90"
                          data-testid="nb-status-save">
                    <Check className="h-4 w-4 mr-1.5" />
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function NBDeltaRow({ delta, onOpen, onEditStatus, onDownload }) {
  const meta = STATUS_META[delta.status] || STATUS_META.pending_review;
  return (
    <div className="vq-card p-4" data-testid={`nb-delta-${delta.delta_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={`text-[10px] font-mono uppercase border ${meta.cls}`}>{meta.label}</Badge>
            <span className="text-[10px] font-mono text-slate-400">{delta.delta_id}</span>
            <Badge className="text-[10px] font-mono uppercase bg-slate-100 border-slate-200 text-slate-700">{delta.change_state}</Badge>
          </div>
          <div className="font-display font-bold text-slate-900 truncate">
            {delta.change_title || delta.change_id}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {(delta.risks || []).length} risk{(delta.risks || []).length === 1 ? "" : "s"} ·
            {" "}{(delta.documents || []).length} document{(delta.documents || []).length === 1 ? "" : "s"} ·
            {" "}Pushed by {delta.pushed_by_email || "—"} · {new Date(delta.pushed_at).toLocaleDateString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onOpen} data-testid={`nb-delta-open-${delta.delta_id}`}>
            Open
          </Button>
          <Button variant="outline" size="sm" onClick={onEditStatus} data-testid={`nb-delta-status-${delta.delta_id}`}>
            Status
          </Button>
          <Button variant="outline" size="sm" onClick={onDownload} data-testid={`nb-delta-pdf-${delta.delta_id}`}>
            <Download className="h-4 w-4 mr-1" />
            PDF
          </Button>
        </div>
      </div>
    </div>
  );
}

function NBDetailSection({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5 text-slate-800 font-semibold text-xs uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5 text-vq-blue" />
        {title}
      </div>
      {children}
    </div>
  );
}
